/*
 * Copyright (c) 2020. American Express Company
 */

import { ChronoUnit, LocalDate } from '@js-joda/core';

import { PersonModel, RelationshipType } from 'src/types/person.model';
import BWDataService from './bw-data.service';
import DateUtils from 'src/utils/date';
import MixpanelService from '../services/mixpanel.service';
import TimeService from './time.service';
import _ from 'lodash';
import { FullUserModel } from 'src/types/user.model';

export default class PersonService extends BWDataService {
    public static async createPerson(personToBeCreated: PersonModel): Promise<PersonModel> {
        if (personToBeCreated.photo) {
            const person: PersonModel = await this.postPerson(personToBeCreated);
            await this.postPhoto(person.personUid, personToBeCreated.photo);
            return person;
        }

        return await this.postPerson(personToBeCreated);
    }

    private static async postPhoto(personId: string, image: Blob): Promise<void> {
        await this.postJpeg(`/photos/person/${personId}/original`, image);
    }

    private static async postPerson(person: PersonModel): Promise<PersonModel> {
        const payload: PersonModel = {
            relationship: person.relationship,
            nickname: person.nickname,
            dob: person.dob,
            isDependent: person.isDependent
        };

        const newPerson = await this.post('/person', payload);
        newPerson.dob = DateUtils.dateify(person.dob);

        MixpanelService.track('Add Person', {
            Type: person.relationship
        });

        return newPerson;
    }

    public static async getPersons(): Promise<PersonModel[]> {
        const persons: PersonModel[] = await this.get('/person');
        persons?.forEach((person) => {
            person.dob = DateUtils.dateify(person.dob);
        });
        return persons;
    }

    public static getPersonsFromUser(user: FullUserModel): PersonModel[] {
        return user.peopleWithPhotos;
    }

    public static async deletePerson(person: PersonModel): Promise<number> {
        return await this.delete(`/person/${person.personUid}`);
    }

    public static async updatePerson(person: PersonModel): Promise<PersonModel> {
        const payload: PersonModel = {
            relationship: person.relationship,
            nickname: person.nickname,
            dob: person.dob,
            isDependent: person.isDependent
        };
        const changedPerson = await this.put(`/person/${person.personUid}`, payload);
        changedPerson.dob = DateUtils.dateify(changedPerson.dob);
        if (person.photo) {
            await this.postPhoto(changedPerson.personUid, person.photo);
        }
        return changedPerson;
    }

    public static getAge(person: PersonModel): number {
        return PersonService.getDOB(person).until(LocalDate.now(), ChronoUnit.YEARS);
    }

    public static getRetireAge(person: PersonModel): LocalDate {
        const age: number = PersonService.getAge(person);
        const retire: number = age > 66 ? age + 5 : 67;
        return TimeService.getLocalDateAtAge(person.dob, retire);
    }

    public static getDieAge(person: PersonModel): LocalDate {
        const age: number = PersonService.getAge(person);
        const die: number = age > 100 ? age + 5 : 100;
        return TimeService.getLocalDateAtAge(person.dob, die);
    }

    public static getEducateAge(person: PersonModel): LocalDate {
        const age: number = PersonService.getAge(person);
        const school: number = age > 18 ? age + 2 : 18;
        return TimeService.getLocalDateAtAge(person.dob, school);
    }

    public static getDOB(person: PersonModel): LocalDate {
        return person.dob || LocalDate.now().minusYears(35);
    }

    public static partner(persons: PersonModel[]): PersonModel {
        return _.find(persons, (person: PersonModel) => {
            return person.relationship === 'spouse' || person.relationship === 'partner';
        });
    }

    public static children(persons: PersonModel[]): PersonModel[] {
        return _.filter(persons, { relationship: RelationshipType.Child });
    }

    public static minorChildren(persons: PersonModel[]): PersonModel[] {
        return _.filter(PersonService.children(persons), (person: PersonModel) => {
            return PersonService.minor(person);
        });
    }

    public static you(persons: PersonModel[]): PersonModel {
        return _.find(persons, { relationship: RelationshipType.Self });
    }

    public static firstName(person: PersonModel): string {
        return person.nickname.split(' ')[0];
    }

    public static minor(person: PersonModel): boolean {
        if (!person.dob) return false;
        return person.dob.until(LocalDate.now(), ChronoUnit.YEARS) < 18;
    }
}
